<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import { GameType } from '@/defines/EnumDefine'
import defaultGameIcon from '@/assets/hall/game_icon_default.webp'
import { ServerDefine } from '@/config/ServerDefine'

const props = defineProps({
  provider: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['close'])

interface GameDisplay {
  id: number
  name: string
  icon: string
  isTop: boolean
  isBuy: boolean
}

const allGames = ref<GameDisplay[]>([])
const displayedResults = ref<GameDisplay[]>([])
const displayCount = ref(12)
const providerName = ref('Search')
const searchQuery = ref('')
const totalGames = ref(0)

const refreshGames = () => {
  if (!props.provider) return

  const info = hall.hallModule.getProviderInfo(props.provider)
  if (info) {
    providerName.value = info.name || 'Games'
  }

  const gameIds = hall.hallModule.getSubgameIDList(GameType.All, props.provider) || []
  totalGames.value = gameIds.length

  allGames.value = gameIds.map((id) => {
    let subInfo = hall.hallModule.getSubgameInfo(id)
    if (!subInfo) {
      subInfo = { gid: id, Name: hall.hallModule.getSubgameName(id) }
    }
    const gid = subInfo.gid || id
    return {
      id: gid,
      name: subInfo.Name || subInfo.name || 'Unknown',
      icon: ServerDefine.GAME_ICON_URL + gid + '.png',
      isTop: hall.hallModule.isHotGame(id),
      isBuy: false,
    }
  })

  applySearch()
}

const applySearch = () => {
  const query = searchQuery.value.toLowerCase().trim()
  let filtered = allGames.value

  if (query) {
    filtered = allGames.value.filter((g) => g.name.toLowerCase().includes(query))
  }

  displayCount.value = 12
  displayedResults.value = filtered
}

const visibleGames = computed(() => {
  return displayedResults.value.slice(0, displayCount.value)
})

const handleLoadMore = () => {
  displayCount.value += 12
}

const clearSearchInput = () => {
  searchQuery.value = ''
  applySearch()
}

const openGame = (gameId: number) => {
  hall.hallModule.postSubgameFree(gameId)
}

const handleImageError = (e: Event) => {
  const target = e.target as HTMLImageElement
  target.onerror = null
  target.src = defaultGameIcon
}

watch(
  () => props.provider,
  () => {
    refreshGames()
  },
)

onMounted(() => {
  refreshGames()
  hall.eventDispatcher.on(hall.EventDefine.RefreshSubgameList, refreshGames)
})

onUnmounted(() => {
  hall.eventDispatcher.off(hall.EventDefine.RefreshSubgameList, refreshGames)
})
</script>

<template>
  <div class="provider-game-list-overlay">
    <div class="header">
      <button class="btn-back" @click="emit('close')">&lt;</button>
      <h2 class="title">{{ providerName }}</h2>
    </div>

    <!-- Search Bar -->
    <div class="search-bar-container">
      <div class="search-bar">
        <input
          type="text"
          v-model="searchQuery"
          @keyup.enter="applySearch"
          placeholder="Search by game name..."
        />
        <button @click="applySearch" class="btn-search">🔍 Search</button>
        <button @click="clearSearchInput" class="btn-clear">🗑️</button>
      </div>
    </div>

    <div class="games-content">
      <div class="results-grid" v-if="visibleGames.length > 0">
        <div
          v-for="game in visibleGames"
          :key="game.id"
          class="game-card item-small cursor-pointer"
          @click="openGame(game.id)"
        >
          <img
            :src="game.icon || defaultGameIcon"
            class="game-img"
            :alt="game.name"
            @error="handleImageError"
          />
          <span v-if="game.isTop" class="tag top">TOP</span>
        </div>
      </div>

      <div v-else class="no-games-placeholder">No games currently available.</div>

      <!-- Pagination Info and Load More -->
      <div class="tray-footer" v-if="displayedResults.length > 0">
        <div class="tray-count">
          Current
          <span class="red-text">{{ visibleGames.length }}/{{ displayedResults.length }}</span>
          Games
        </div>
        <button
          v-if="displayedResults.length > visibleGames.length"
          @click="handleLoadMore"
          class="btn-load-more-tray"
        >
          Load More
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.provider-game-list-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--theme-nav-bg);
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .provider-game-list-overlay {
    left: 80px;
    width: calc(100vw - 80px);
    top: 56px;
    height: calc(100vh - 56px);
  }
}

.header {
  display: flex;
  align-items: center;
  padding: 15px;
  background: var(--theme-lobby-bg);
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid var(--theme-border-card);
}

.btn-back {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0 10px 0 0;
}

.title {
  flex: 1;
  text-align: center;
  margin: 0;
  padding-right: 25px; /* offset back button */
  color: #fff;
  font-size: 1.2rem;
}

.search-bar-container {
  padding: 15px 10px;
  /* background: var(--theme-nav-bg); */
}

@media (min-width: 768px) {
  .search-bar-container {
    padding: 15px 100px;
    display: flex;
    justify-content: flex-end;
  }

  .search-bar {
    width: 25%;
  }
}

.search-bar {
  display: flex;
  gap: 8px;
  background: var(--theme-tab-inactive);
  border-radius: 6px;
  padding: 4px;
}

.search-bar input {
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  padding-left: 10px;
  outline: none;
}

.btn-search {
  background: var(--theme-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-weight: bold;
  cursor: pointer;
}

.btn-clear {
  background: #333;
  border: none;
  color: #888;
  padding: 0 10px;
  border-radius: 4px;
  cursor: pointer;
}

.games-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

@media (min-width: 768px) {
  .games-content {
    padding: 10px 100px;
  }
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

@media (min-width: 768px) {
  .results-grid {
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;
  }
}

.game-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  background: transparent;
}

.game-img {
  width: 100%;
  height: auto;
  display: block;
}

.tag {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 2;
}

.tag.top {
  top: 5px;
  left: 5px;
  background: #ff4500;
  color: white;
}

.tray-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 30px;
  gap: 15px;
}

.tray-count {
  font-size: 1rem;
  color: #888;
}

.red-text {
  color: var(--theme-accent);
  font-weight: bold;
}

.btn-load-more-tray {
  background: var(--theme-primary);
  color: white;
  border: none;
  padding: 12px 40px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.btn-load-more-tray:active {
  transform: scale(0.95);
}

.no-games-placeholder {
  text-align: center;
  color: #888;
  margin-top: 50px;
  font-size: 1.1rem;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
